import { importLegacySession } from '@wheely/web-auth-lib/core';
import type { AuthClient } from '@wheely/web-auth-lib/core';

import { deleteCookie } from '@/shared/utils/cookie';
import { AUTH_COOKIE_KEY, getSessionFromCookie } from '@/shared/utils/session';

import { authAPI } from './api';
import { AUTH_MEDIATOR_BASE } from './api/v3';
import { cleanupLegacySession, readLegacySession } from './legacySession';
import { AUTH_MODES, getAuthMode } from './mode';

/**
 * Picks the session the app starts with, in the one order that cannot swap the
 * visitor's identity: the V3 session is restored first, and only a visitor
 * without one is offered to the legacy stacks.
 *
 * Returns the one thing the caller cannot read anywhere else: whether the legacy
 * AuthProvider starts with a session of its own. It is not the visitor's auth
 * state — a restored V3 session is authenticated and still answers false here,
 * because that session belongs to the V3 client, which holds it itself.
 */
export const bootstrapSession = async (
  client: Pick<AuthClient, 'applyTokens' | 'restoreSession'>,
): Promise<{ legacyIsAuthenticated: boolean }> => {
  let v3Session;
  let restoreAnswered = true;

  // Restored before anything legacy is touched: this is what tells a visitor
  // who already has a V3 session — anyone who just signed in or registered —
  // apart from a pre-migration visitor who has none. The V3 provider restores
  // on mount as well, but awaiting here keeps the first render settled: no
  // authenticated → login flicker on reload.
  try {
    v3Session = await client.restoreSession();
  } catch {
    // A throw is not an empty answer: the mediator never said whether this
    // visitor has a session. Nothing legacy may run on a maybe — that is how a
    // stale copy would take over a session that does exist.
    restoreAnswered = false;
  }

  // The legacy copies outlive the account they were issued for: the hand-off
  // cookie sits on wheely.com until it expires, and the stored record keeps
  // answering `legacy` to the mode detector. Either one, left alone next to a
  // fresh V3 session, would hand the app the previous visitor's identity — a
  // registration completed on this device would end up browsing the account
  // that used it last. Wiped instead of imported, and nothing legacy runs.
  if (v3Session) {
    await cleanupLegacySession();

    return { legacyIsAuthenticated: false };
  }

  // The copies are kept here rather than wiped: a start that could not ask is
  // not proof that this visitor has nothing to promote. The next one retries.
  if (!restoreAnswered) {
    return { legacyIsAuthenticated: false };
  }

  // One-shot promotion of a legacy (V6) session — the hand-off cookie set by
  // wheely.com or the stored session of a pre-migration visitor — into the
  // mediated V3 stack (§6.6). On success the legacy copies are wiped and the
  // mode flips to V3 for good; a transient failure leaves them for the legacy
  // stack below to serve, and the import retries on the next start.
  const imported = await importLegacySession(client, {
    mediatorBase: AUTH_MEDIATOR_BASE,
    read: readLegacySession,
    cleanup: cleanupLegacySession,
  });

  if (imported) {
    return { legacyIsAuthenticated: false };
  }

  let legacySession;

  const savedCookieSession = getSessionFromCookie();

  try {
    if (savedCookieSession) {
      deleteCookie(AUTH_COOKIE_KEY);
      legacySession = await authAPI.setCurrentSession(savedCookieSession);
    } else if (getAuthMode() === AUTH_MODES.LEGACY) {
      legacySession = await authAPI.restoreSession();
    }
  } catch {
    legacySession = undefined;
  }

  return { legacyIsAuthenticated: Boolean(legacySession?.activated) };
};
